import { getCompactRangeSelectorOptions, getRangeSelector } from './rangeSelectorConfig';
import { yearGridlineXAxisOptions } from './featureFlags';

/**
 * True when the viewport is short and wide the way a phone in landscape
 * (or a small landscape tablet) is — as opposed to merely "narrow" (a
 * portrait phone, which `maxWidth`/`minWidth` conditions already catch).
 * Chart `height` here is set as a percentage of the chart's own *width*
 * (Highcharts' documented behavior for percentage heights), so a wide
 * landscape phone would otherwise compute a very tall chart (e.g. 65% of
 * an 900px-wide chart = ~585px) that blows past the ~300-380px of actual
 * vertical space a landscape phone viewport has. Guarded for environments
 * without `window` (SSR/tests).
 */
const isShortLandscapeViewport = () => (
  typeof window !== 'undefined' && window.innerHeight <= 500
);

/**
 * Formats a dollar value compactly (e.g. $1.23B) for company-total display
 * scale. Used for y-axis labels and tooltips when displayScale === 'total'.
 */
export const formatCompactCurrency = (value) => {
    if (value == null || Number.isNaN(value)) return '';
    const abs = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    if (abs >= 1e12) return `${sign}$${(abs / 1e12).toFixed(2)}T`;
    if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(2)}B`;
    if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(2)}M`;
    return `${sign}$${abs.toFixed(2)}`;
};

/**
 * Primary y-axis for the main chart. `displayScale` controls label formatting
 * (compact currency for company totals, plain numbers per-share). `axisScale`
 * switches between a linear and a logarithmic axis.
 *
 * `floor` is set explicitly for both scales, never omitted: `chart.update()`
 * merges options, so a leftover `floor: 0` would survive a switch to log and
 * clip every value below $1. See ADR 0002.
 */
export const getYAxis = (displayScale = 'perShare', axisScale = 'linear') => [
    {
        type: axisScale === 'logarithmic' ? 'logarithmic' : 'linear',
        floor: axisScale === 'logarithmic' ? null : 0,
        labels: displayScale === 'total'
            ? { formatter: function () { return formatCompactCurrency(this.value); } }
            : {},
    },
    {
        height: "80%",
    },
    {
        top: "80%",
        height: "20%",
        offset: 0,
        min: 0,
    },
];

/**
 * Series tooltip formatting depends on displayScale: compact currency for
 * company totals, 2-decimal-place plain values for per-share (unchanged
 * default behavior).
 */
// The implied-estimate methodology caption that used to be built here (keyed
// off the "implied:<anchor>" estimateKind) has moved to the page-bottom
// disclaimer footer (config/disclaimers.js, footnote "implied-estimates").
// The in-chart estimated-region plot band (seriesMath.js's
// buildEstimatedPlotBand) stays — a single grey band with a footnote-marked
// "Estimated¹" label — that's a concise data annotation, not the verbose
// disclaimer paragraph.

export const getValueTooltip = (displayScale = 'perShare') => (
    displayScale === 'total'
        ? { pointFormatter: function () { return `<span style="color:${this.color}">●</span> ${this.series.name}: <b>${formatCompactCurrency(this.y)}</b><br/>`; } }
        : { valueDecimals: 2 }
);

/**
 * Binary-searches a series' own points for the one nearest (by x) to the
 * hovered timestamp `x`. Returns null for an empty/untracked series. Used by
 * the shared tooltip below so a series drawn on sparse timestamps (the
 * fair-value line — see useStockData.js, deliberately NOT resampled onto
 * Price's dense daily grid) still contributes a row at every hovered date,
 * showing its nearest *actual reported* point rather than dropping out of
 * the shared tooltip entirely (which is what fractured it into separate,
 * misaligned boxes once the line stopped sharing Price's x-values).
 */
const nearestPoint = (points, x) => {
    if (!points || !points.length) return null;
    let lo = 0;
    let hi = points.length - 1;
    while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (points[mid].x < x) lo = mid + 1;
        else hi = mid;
    }
    if (lo > 0 && Math.abs(points[lo - 1].x - x) <= Math.abs(points[lo].x - x)) {
        return points[lo - 1];
    }
    return points[lo];
};

/**
 * The main chart's shared tooltip. Highcharts' built-in `shared: true` only
 * merges series that have a point at the *exact* hovered x — but the
 * fair-value line now sits on its metric's own sparse report timestamps, so
 * it almost never shares Price's daily x and kept dropping out into its own
 * separate, offset box. This formatter instead drives the whole box itself:
 * for the hovered x (taken from the Price point Highcharts does have), it
 * looks up each mouse-tracked series' *nearest actual point* and renders one
 * merged box. Each series supplies its own row markup via
 * `series.options.custom.tooltipRow(point)`; series without one (or with no
 * points near x) are skipped. The header is the hovered Price point's own
 * date, formatted by Highcharts' default date logic.
 */
export const getMainChartTooltip = () => ({
    shared: true,
    formatter: function () {
        // `this.points` is whatever Highcharts matched at the hovered x —
        // in practice the Price line (dense/daily). Anchor everything to
        // that x so the fair-value/avg lines resolve to the same date.
        const anchor = (this.points && this.points[0]) || this.point;
        if (!anchor) return false;
        const x = anchor.x;
        // Format the header date via the chart's own `time` object (its own
        // Highcharts instance), so this file needs no direct Highcharts
        // import and can't pull in a second copy.
        const header = `<span style="font-size:11px">${anchor.series.chart.time.dateFormat('%A, %e %b %Y', x)}</span><br/>`;

        const rows = anchor.series.chart.series
            .filter((s) => s.visible && s.options.enableMouseTracking !== false)
            .map((s) => {
                const formatRow = s.options.custom && s.options.custom.tooltipRow;
                if (!formatRow) return '';
                const p = nearestPoint(s.points, x);
                if (!p) return '';
                // Pass the hovered x so a row can tell it's being shown for a
                // date beyond its own series' data (nearestPoint clamps to the
                // last point). The Price row uses this to drop out in the
                // estimate/forecast region, where price has no real coverage
                // and would otherwise repeat its last actual value at every
                // future date. Rows that don't care simply ignore the arg.
                return formatRow(p, x);
            })
            .filter(Boolean);

        return rows.length ? header + rows.join('') : false;
    },
});

export const getDefaultChartOptions = (handlePointClick, displayScale = 'perShare', axisScale = 'linear') => ({
    yAxis: getYAxis(displayScale, axisScale),
    xAxis: [{
        crosshair: false,
        ordinal: false,
        // Vertical per-year gridlines, gated behind the VERTICAL_YEAR_GRIDLINES
        // feature flag (currently off — see config/featureFlags.js).
        ...yearGridlineXAxisOptions(),

    }],
    chart: {
        animation: true,
        height: '65%',
        zIndex: 0,
        zoomType: 'x',
    },
    responsive: {
        rules: [
            {
                // Phones and small tablets, either orientation: reclaim
                // horizontal space and keep text at a legible floor size
                // rather than letting the button row/date inputs wrap or
                // overflow the container.
                condition: {
                    maxWidth: 768
                },
                chartOptions: {
                    chart: {
                        spacingLeft: 4,
                        spacingRight: 4,
                        // `chart.height: '65%'` (below) is a percentage of the
                        // chart's own *width*, so once the container itself is
                        // narrow (a phone or tablet in portrait), 65% of that
                        // narrow width is nowhere near enough absolute pixels
                        // to fit the range-selector row + legend + navigator
                        // that all still need to render above/below the
                        // actual price/EPS plot — confirmed via real-browser
                        // screenshots to collapse the plot area to 0px height,
                        // leaving only the chrome visible. A fixed floor here
                        // guarantees room for an actual plot regardless of how
                        // narrow the container ends up. 280 itself still only
                        // left ~77px of actual plot area once the
                        // range-selector row + legend + navigator were
                        // accounted for (confirmed via real-browser
                        // Playwright screenshot) — visible, but thin enough
                        // to read as "pancake flat." 320 leaves more legible
                        // room for the same chrome.
                        // Maintainer's real-device retest (2026-08-23) still
                        // wanted this noticeably bigger; 420 (+31% over 320)
                        // still leaves the range-selector/legend/navigator
                        // chrome unsqueezed (confirmed via real-browser
                        // Playwright screenshot).
                        height: 420
                    },
                    legend: {
                        align: 'center',
                        verticalAlign: 'bottom',
                        itemDistance: 8,
                        itemMarginBottom: 4,
                        padding: 4,
                        itemStyle: { fontSize: '11px' }
                    },
                    rangeSelector: getCompactRangeSelectorOptions(),
                    xAxis: [{
                        labels: { style: { fontSize: '10px' } }
                    }],
                    yAxis: [{
                        labels: { style: { fontSize: '10px' } }
                    }],
                    navigator: {
                        // Bigger drag handles for touch; default 7x15 is a
                        // fine-motor-precision target on a phone.
                        handles: { width: 10, height: 18 }
                    }
                }
            },
            {
                // Landscape phones / short viewports: see
                // isShortLandscapeViewport's docstring above for why the
                // default width-relative height needs an explicit
                // override here instead.
                condition: {
                    minWidth: 601,
                    maxWidth: 1024,
                    callback: function () { return isShortLandscapeViewport(); }
                },
                chartOptions: {
                    // 220 itself still only left ~33px of actual plot area
                    // once the range-selector row + legend + navigator were
                    // accounted for (confirmed via real-browser Playwright
                    // screenshot) — thin enough to read as "pancake flat."
                    // 260 leaves more legible room within a landscape
                    // phone's limited vertical budget.
                    // Maintainer's real-device retest (2026-08-23) still
                    // wanted this noticeably bigger; 330 (+27% over 260)
                    // stays comfortably under a landscape phone's ~375-390px
                    // innerHeight, so the whole chart (chrome included) is
                    // still viewable without itself exceeding the viewport
                    // (confirmed via real-browser Playwright screenshot at
                    // 812x375 / 844x390).
                    chart: { height: 330 },
                    navigator: { height: 26 }
                }
            }
        ]
    },
    series: [
        {
            name: 'Price',
            // Stable id so HighchartsReact's oneToOne update matches this by
            // identity to buildMainChartSeries' value line — not by array
            // position. Every main-chart series carries a unique id (see the
            // detailed oneToOne writeup in valuationChartConfig.js) so the
            // dividend overlay's conditional presence can no longer shift a
            // positional match and bleed its styling onto a neighbor.
            id: 'value-line',
            findNearestPointBy: 'xy',
            // Price sits ABOVE every other main-chart series, including the
            // valuation avg line (fairvalue-avg-metric, zIndex 102) and its
            // ±1σ band (101). It's the primary reference on this chart, so it
            // must never be obscured by the overlays drawn against it. Kept in
            // sync with the rebuilt price line's zIndex in useStockData.js's
            // buildMainChartSeries.
            zIndex: 200,
            lineWidth: 2,
            color: "black",
            tooltip: getValueTooltip(displayScale),
            // The real value line (useStockData.js buildMainChartSeries) now
            // feeds object-format {x, y, ratio} points (ratio drives the P/E
            // bracket in the shared tooltip). Highcharts' turbo mode silently
            // rejects non-array data (console error #12), and Series.update()
            // uses the *pre-update* options — so this must be set here on the
            // series the chart is created with, not just on the new options
            // (same reasoning as the 'Earnings × Multiple' placeholder below).
            turboThreshold: 0,
        },
        {
            name: 'Earnings × Multiple',
            // Stable id matching buildMainChartSeries' "× Multiple" fair-value
            // line, so the oneToOne update matches by identity (see the
            // 'value-line' note above and valuationChartConfig.js).
            id: 'fairvalue-metric-line',
            type: 'areaspline',
            lineColor: "orange",
            lineWidth: 4,
            color: "green",
            tooltip: getValueTooltip(displayScale),
            // The navigator's mini-axis is built from the series that opt into
            // it. The Price base series stops at the last *actual* price point,
            // but the main chart's default window (computeDefaultRange) extends
            // right to this fair-value series' forward estimate tail. Left to
            // Price alone, the navigator's axis spans a narrower range than the
            // main chart, so its handles/labels no longer line up with the plot
            // above (the reported "range slider doesn't match the chart area"
            // bug). Opting this forward-reaching series into the navigator makes
            // the navigator's data extent match the main chart's.
            showInNavigator: true,
            // This placeholder series is later updated in place (matched by
            // its stable 'fairvalue-metric-line' id, not by array position —
            // see the id note above) with the real fair-value line built
            // in useStockData.js's buildMainChartSeries, whose {x,y,rawValue}
            // object-format data Highcharts' turbo mode silently rejects
            // (console error #12). Series.update() calls setData() using the
            // *pre-update* options — before useStockData.js's own
            // turboThreshold: 0 on the new series options gets merged in — so
            // that per-update override alone doesn't help this specific
            // series on the very first load; it must already be set here, on
            // the series Highcharts creates the chart with. Without it, the
            // line never renders — this was the main-chart "EPS line
            // disappeared" regression.
            turboThreshold: 0,
            // Triangle markers on each real report/estimate point, joined by
            // the orange line + green areaspline fill. This is the intended
            // look: the fair-value series is drawn from the metric's own
            // sparse reported points (see useStockData.js — it is
            // deliberately NOT resampled onto Price's dense daily grid), so a
            // marker per point reads as a clear data point rather than the
            // dense-daily noise a resampled series would produce. Both the
            // historical points and the forward TTM/estimate tail get one.
            //
            // Clickability: the returns-measure feature (handlePointClick.js)
            // is driven by clicking these triangles, but they were hard to
            // hit — worst of all where the black Price line (zIndex 200)
            // crosses directly over a triangle, since that higher-z line
            // otherwise intercepts the click. Two things widen the effective
            // hit target without changing the drawn look at rest:
            //   1. findNearestPointBy: 'x' lets a click anywhere in the
            //      point's vertical column register on this series, instead
            //      of requiring near-exact 2D proximity to the small (r=5)
            //      triangle — the default 'xy' for line/areaspline made the
            //      triangle a tiny 2D target.
            //   2. A hover state that enlarges the marker gives real feedback
            //      and a bigger grab area as the cursor approaches.
            findNearestPointBy: 'x',
            marker: {
                enabled: true,
                radius: 5,
                symbol: 'triangle',
                fillColor: 'white',
                lineColor: 'black',
                lineWidth: 1,
                states: {
                    hover: {
                        enabled: true,
                        radiusPlus: 4,
                        lineWidthPlus: 1,
                    },
                },
            },
        },
        {
            name: "performance",
            id: "performance-line",
            type: "line",
            dashStyle: 'LongDash',
            lineColor: "darkgrey",
            enableMouseTracking: false,
            showInLegend: false,
            marker: {
                enabled: true,
                radius: 5,
                symbol: 'circle',
                fillColor: 'red',
                lineColor: 'red',
                lineWidth: 1,
            },
        }
    ],
    tooltip: getMainChartTooltip(),
    // Highcharts' stockChart constructor forces legend.enabled: false
    // internally regardless of the core default — must be re-enabled
    // explicitly now that the per-metric avg fair-value lines need a
    // legend entry to distinguish them from the manual multiple line.
    legend: {
        enabled: true,
    },
    plotOptions: {
        series: {
            states: {
                hover: { lineWidthPlus: 0 },
                inactive: { enabled: false },
            },
            point: {
                events: {
                    click: handlePointClick
                }
            }
        }
    },
    // No explicit index here -- getRangeSelector()'s own default now
    // resolves to the real "15y" button (derived from RANGE_SELECTOR_BUTTONS
    // itself, not a hardcoded number that can drift out of sync with it).
    rangeSelector: getRangeSelector(),
});
